import type { CalibrationSession, CalibrationSummary } from '../experiments/Calibration.ts';
import { button, el, fmtMs, fmtPct, numberField, panel } from './dom.ts';

export interface CalibrationPanelCallbacks {
  onStart: () => void;
  onStop: () => void;
  /** Push the measured timings into the bot's parameters. */
  onApply: (summary: CalibrationSummary) => void;
}

/**
 * Calibration screen.
 *
 * Measures the participant's response timing so the bot can be told what it is
 * actually driving. It does not, and must not, adjust stimulation intensity --
 * the panel says so on screen, because the distinction matters more to a person
 * reading the UI than to a person reading the source.
 *
 * The measured values are presented as *suggestions* with an explicit Apply
 * button rather than being pushed into the controller automatically. A
 * calibration run with three responses and seventeen misses produces a
 * confident-looking median, and an operator should see that before it becomes
 * the number the autonomous controller plans against.
 */
export class CalibrationPanel {
  readonly root: HTMLElement;

  private statusEl: HTMLElement;
  private progressEl: HTMLElement;
  private resultsEl: HTMLElement;
  private histogramEl: HTMLElement;
  private startBtn: HTMLButtonElement;
  private stopBtn: HTMLButtonElement;
  private applyBtn: HTMLButtonElement;
  private lastSummary: CalibrationSummary | null = null;

  constructor(
    private readonly getSession: () => CalibrationSession | null,
    cb: CalibrationPanelCallbacks,
  ) {
    const p = panel('Calibration', 'panel-calibration');
    this.root = p.root;

    p.body.appendChild(
      el(
        'p',
        'hint',
        'Measures response TIMING only. It never changes stimulation intensity, electrode ' +
          'placement, or waveform — those are set by hand on the hardware, with the ' +
          'participant present, following the manufacturer’s procedure.',
      ),
    );

    const opts = el('div', 'calibration-options');
    p.body.appendChild(opts);
    const bindOption = (
      label: string,
      get: () => number,
      set: (v: number) => void,
      unit?: string,
    ) => {
      opts.appendChild(
        numberField(label, get(), (v) => set(v), { min: 1, step: 10, unit }).row,
      );
    };
    bindOption('Trials', () => this.opts()?.trials ?? 20, (v) => {
      const o = this.opts();
      if (o) o.trials = Math.max(1, Math.round(v));
    });
    bindOption('Inter-trial interval', () => this.opts()?.interTrialIntervalMs ?? 1500, (v) => {
      const o = this.opts();
      if (o) o.interTrialIntervalMs = v;
    }, 'ms');
    bindOption('Inter-trial jitter', () => this.opts()?.interTrialJitterMs ?? 700, (v) => {
      const o = this.opts();
      if (o) o.interTrialJitterMs = v;
    }, 'ms');
    bindOption('Response timeout', () => this.opts()?.responseTimeoutMs ?? 800, (v) => {
      const o = this.opts();
      if (o) o.responseTimeoutMs = v;
    }, 'ms');

    const row = el('div', 'button-row');
    this.startBtn = button('Start calibration', cb.onStart);
    this.stopBtn = button('Stop', cb.onStop, 'btn-secondary');
    this.applyBtn = button('Apply measured timing to bot', () => {
      if (this.lastSummary) cb.onApply(this.lastSummary);
    }, 'btn-primary');
    row.append(this.startBtn, this.stopBtn, this.applyBtn);
    p.body.appendChild(row);

    this.statusEl = el('div', 'calibration-status', 'Idle. Arm the stimulator to begin.');
    this.progressEl = el('div', 'calibration-progress');
    this.resultsEl = el('div', 'fields');
    this.histogramEl = el('div', 'histogram');
    p.body.append(this.statusEl, this.progressEl, this.histogramEl, this.resultsEl);
  }

  private opts() {
    return this.getSession()?.options ?? null;
  }

  update(): void {
    const session = this.getSession();
    if (!session) {
      this.statusEl.textContent = 'Calibration is available in stimulation modes only.';
      this.startBtn.disabled = true;
      this.stopBtn.disabled = true;
      this.applyBtn.disabled = true;
      return;
    }

    const { done, total } = session.progress;
    this.startBtn.disabled = session.isRunning;
    this.stopBtn.disabled = !session.isRunning;

    if (session.isRunning) {
      this.statusEl.textContent = session.isAwaitingResponse
        ? 'Stimulus delivered — waiting for a response…'
        : 'Waiting for the next trial…';
      this.statusEl.className = `calibration-status ${session.isAwaitingResponse ? 'awaiting' : ''}`;
    } else if (done > 0) {
      this.statusEl.textContent = `Finished: ${done} trials.`;
      this.statusEl.className = 'calibration-status';
    }

    this.progressEl.replaceChildren();
    for (let i = 0; i < total; i++) {
      const trial = session.trials[i];
      const dot = el('span', `dot ${trial ? trial.outcome : 'pending'}`);
      dot.title = trial
        ? `#${i}: ${trial.outcome}${trial.latencyMs !== null ? ` ${trial.latencyMs.toFixed(0)} ms` : ''}`
        : `#${i}: pending`;
      this.progressEl.appendChild(dot);
    }

    if (done === 0) {
      this.applyBtn.disabled = true;
      this.resultsEl.replaceChildren();
      this.histogramEl.replaceChildren();
      return;
    }

    const summary = session.summarise();
    this.lastSummary = summary;
    this.applyBtn.disabled = summary.responded < 3;
    this.applyBtn.title =
      summary.responded < 3 ? 'Need at least 3 responses before applying' : '';

    this.renderHistogram(session.trials.map((t) => t.latencyMs).filter((v): v is number => v !== null));

    const rows: [string, string, boolean?][] = [
      ['Responded', `${summary.responded} / ${summary.trials}`],
      ['Miss rate', fmtPct(summary.missRate), summary.missRate > 0.2],
      ['Rejected by safety layer', String(summary.rejected), summary.rejected > 0],
      ['Median latency', fmtMs(summary.medianMs)],
      ['Mean latency', fmtMs(summary.meanMs)],
      ['p05 – p95', `${fmtMs(summary.p05Ms)} – ${fmtMs(summary.p95Ms)}`],
      ['Jitter (σ)', fmtMs(summary.stdDevMs)],
      ['Range', `${fmtMs(summary.minMs)} – ${fmtMs(summary.maxMs)}`],
      ['→ bot latency', fmtMs(summary.suggestedLatencyMs)],
      ['→ in-flight grace', fmtMs(summary.suggestedGraceMs)],
      ['→ min stimulus interval', fmtMs(summary.suggestedMinIntervalMs)],
    ];
    this.resultsEl.replaceChildren();
    for (const [label, value, warn] of rows) {
      const row = el('div', 'field');
      row.append(
        el('span', 'field-label', label),
        el('span', `field-value${warn ? ' tone-warn' : ''}`, value),
      );
      this.resultsEl.appendChild(row);
    }
  }

  /** Latency histogram: the shape matters as much as the median. */
  private renderHistogram(latencies: number[]): void {
    this.histogramEl.replaceChildren();
    if (latencies.length < 2) return;
    const min = Math.min(...latencies);
    const max = Math.max(...latencies);
    const span = Math.max(1, max - min);
    const bins = 12;
    const counts = new Array<number>(bins).fill(0);
    for (const l of latencies) {
      counts[Math.min(bins - 1, Math.floor(((l - min) / span) * bins))]++;
    }
    const peak = Math.max(...counts, 1);
    for (let i = 0; i < bins; i++) {
      const bar = el('div', 'histogram-bar');
      bar.style.height = `${(counts[i] / peak) * 100}%`;
      const lo = min + (i / bins) * span;
      bar.title = `${lo.toFixed(0)}–${(lo + span / bins).toFixed(0)} ms: ${counts[i]}`;
      this.histogramEl.appendChild(bar);
    }
  }
}
